import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth, useCart } from "@/lib/store";
import { toast } from "sonner";
import { Package, Truck, ExternalLink, RotateCcw, Loader2, ShoppingBag } from "lucide-react";

/**
 * Order History for logged-in customers.
 * - Lists past paid orders (most recent first)
 * - Shows shipping status, tracking number and a link to UPS tracking
 * - One-click "Reorder" adds all items back to the cart, then opens the drawer
 */

const STATUS_KEYS = {
  paid: "orders.status_paid",
  processing: "orders.status_processing",
  shipped: "orders.status_shipped",
  delivered: "orders.status_delivered",
  rma_requested: "orders.status_rma_requested",
  refunded: "orders.status_refunded",
  cancelled: "orders.status_cancelled",
};

const STATUS_TONE = {
  paid: "bg-navy text-cream",
  processing: "bg-royal text-white",
  shipped: "bg-royal text-white",
  delivered: "bg-navy text-cream",
  refunded: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
  rma_requested: "bg-royal text-white",
};

function relativeDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "en" ? "en-US" : "es-ES", { day: "2-digit", month: "long", year: "numeric" });
}

export default function OrderHistory() {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { setItems, setOpen } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login"); return; }
    if (user) {
      api.get("/orders/mine")
        .then(({ data }) => setOrders(data))
        .catch(() => { setOrders([]); toast.error(i18n.language === "en" ? "Could not load orders" : "No se pudieron cargar los pedidos"); });
    }
  }, [user, authLoading, navigate, i18n.language]);

  const handleReorder = async (orderId) => {
    setReorderingId(orderId);
    try {
      const { data } = await api.post(`/orders/${orderId}/reorder`);
      setItems(data.items || []);
      if (data.skipped > 0) {
        toast.warning(i18n.language === "en"
          ? `${data.skipped} product(s) are no longer available and were skipped`
          : `${data.skipped} producto(s) ya no están disponibles y se omitieron`);
      } else {
        toast.success(i18n.language === "en" ? "Added to your bag" : "Añadido a tu bolsa");
      }
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || (i18n.language === "en" ? "Could not reorder" : "No se pudo repetir el pedido"));
    } finally {
      setReorderingId(null);
    }
  };

  if (authLoading || orders === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 strokeWidth={1.25} className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="order-history-page">
      <div className="mb-10">
        <div className="font-mono-caps text-xs text-muted-foreground">
          <Link to="/account" className="hover:text-navy">{t("nav.account")}</Link> · {t("account.orders")}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl mt-3">{t("orders.title")}</h1>
        <p className="text-muted-foreground mt-3">
          {t("orders.subtitle")}
        </p>
      </div>

      {orders.length === 0 && (
        <div className="border border-border p-16 text-center bg-cream/40" data-testid="orders-empty">
          <ShoppingBag strokeWidth={1.25} className="h-12 w-12 mx-auto text-muted-foreground mb-6" />
          <h2 className="font-serif text-2xl mb-3">{t("orders.empty_title")}</h2>
          <p className="text-muted-foreground mb-8">{t("orders.empty_copy")}</p>
          <Link to="/shop" className="inline-block bg-navy text-cream px-8 py-4 font-mono-caps hover:bg-cream hover:text-navy border border-navy transition-colors">
            {t("orders.empty_cta")}
          </Link>
        </div>
      )}

      <div className="space-y-6">
        {orders.map((o) => {
          const items = o.items || [];
          const trackingUrl = o.tracking_number
            ? `https://www.ups.com/track?loc=${i18n.language === "en" ? "en_US" : "es_ES"}&tracknum=${encodeURIComponent(o.tracking_number)}`
            : null;
          const status = o.status || "paid";
          const badgeCls = STATUS_TONE[status] || "bg-muted text-muted-foreground";
          return (
            <article
              key={o.order_id}
              className="border border-border bg-white"
              data-testid={`order-${o.order_id}`}
            >
              <header className="flex flex-wrap items-start justify-between gap-4 p-6 border-b border-border">
                <div>
                  <div className="font-mono-caps text-xs text-muted-foreground">{t("orders.order")}</div>
                  <div className="font-serif text-lg mt-1">#{o.order_id.slice(-8).toUpperCase()}</div>
                  <div className="text-xs text-muted-foreground mt-1">{relativeDate(o.created_at, i18n.language)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-block font-mono-caps text-[10px] px-3 py-1 ${badgeCls}`} data-testid={`order-status-${o.order_id}`}>
                    {t(STATUS_KEYS[status] || `orders.status_${status}`)}
                  </span>
                  <div className="text-right">
                    <div className="font-mono-caps text-[10px] text-muted-foreground">{t("orders.total")}</div>
                    <div className="font-serif text-xl">${Number(o.amount).toFixed(2)} {(o.currency || "USD").toUpperCase()}</div>
                  </div>
                </div>
              </header>

              <div className="p-6 grid gap-4">
                {items.map((it, i) => (
                  <div key={i} className="flex gap-4 items-center">
                    {it.image ? (
                      <Link to={`/producto/${it.slug || ""}`} className="flex-shrink-0">
                        <img src={it.image} alt={it.name} className="w-16 h-20 object-contain bg-muted p-1" />
                      </Link>
                    ) : (
                      <div className="w-16 h-20 bg-muted" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-caps text-[10px] text-muted-foreground">{it.brand}</div>
                      {it.slug ? (
                        <Link to={`/producto/${it.slug}`} className="font-serif text-lg leading-tight hover:text-navy">{it.name}</Link>
                      ) : (
                        <div className="font-serif text-lg leading-tight">{it.name}</div>
                      )}
                      <div className="text-xs text-muted-foreground">{it.size_ml}ml · {t("orders.quantity")} {it.qty}</div>
                    </div>
                    <div className="text-sm text-muted-foreground whitespace-nowrap">
                      ${(it.unit_price * it.qty).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <footer className="flex flex-wrap items-center justify-between gap-4 p-6 border-t border-border bg-cream/30">
                <div className="text-xs">
                  {o.tracking_number ? (
                    <div className="flex items-center gap-2">
                      <Truck strokeWidth={1.25} className="h-4 w-4 text-navy" />
                      <div>
                        <span className="font-mono-caps text-muted-foreground">{o.shipping_carrier || (i18n.language === "en" ? "Shipping" : "Envío")}:</span>{" "}
                        <a
                          href={trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-navy inline-flex items-center gap-1"
                          data-testid={`order-tracking-${o.order_id}`}
                        >
                          {o.tracking_number} <ExternalLink strokeWidth={1.25} className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Package strokeWidth={1.25} className="h-4 w-4" /> {t("orders.no_tracking")}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleReorder(o.order_id)}
                  disabled={reorderingId === o.order_id}
                  className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 font-mono-caps text-xs hover:bg-cream hover:text-navy border border-navy transition-colors disabled:opacity-50"
                  data-testid={`order-reorder-${o.order_id}`}
                >
                  {reorderingId === o.order_id ? (
                    <><Loader2 strokeWidth={1.25} className="h-3 w-3 animate-spin" /> {t("orders.reorder_loading")}</>
                  ) : (
                    <><RotateCcw strokeWidth={1.25} className="h-3 w-3" /> {t("orders.reorder")}</>
                  )}
                </button>
              </footer>
            </article>
          );
        })}
      </div>
    </div>
  );
}
